/**
 * Blackjack shop catalog, case odds, and crate-reel helpers.
 *
 * Prices, rarities, and drop tables match the legacy blackjack cosmetics service.
 * Skin *looks* live as SVG motifs in the web app; this file is ids, economy, and RNG only.
 */

export const DEFAULT_SKIN_ID = "classic";

export const CASE_KEY_COST = 1;

export const REEL_LENGTH = 42;
export const REEL_WINNER_INDEX = 36;

export type SkinRarity = "Common" | "Uncommon" | "Rare" | "Epic" | "Legendary" | "Special";
export type ChipRarity = "Common" | "Uncommon" | "Rare" | "Epic" | "Legendary" | "Mega Jackpot";

export interface SkinDef {
  id: string;
  name: string;
  rarity: SkinRarity;
  /** Chip price in the shop. `null` means case-only. */
  price: number | null;
  /** Chips paid when this skin drops as a duplicate. */
  duplicateValue: number;
}

export interface CaseDef {
  id: "gold" | "skins";
  name: string;
  type: "chips" | "skins";
  price: 0;
  requiresKey: true;
}

export const SKIN_CATALOG: readonly SkinDef[] = [
  { id: "classic", name: "Classic", rarity: "Common", price: 0, duplicateValue: 0 },
  { id: "ocean", name: "Ocean", rarity: "Common", price: 250, duplicateValue: 125 },
  { id: "fire", name: "Fire", rarity: "Common", price: 250, duplicateValue: 125 },
  { id: "ice", name: "Ice", rarity: "Uncommon", price: 600, duplicateValue: 300 },
  { id: "lightning", name: "Lightning", rarity: "Uncommon", price: 700, duplicateValue: 350 },
  { id: "steel", name: "Steel", rarity: "Uncommon", price: 600, duplicateValue: 300 },
  { id: "crystal", name: "Crystal", rarity: "Rare", price: 1400, duplicateValue: 700 },
  { id: "desert", name: "Desert", rarity: "Rare", price: 1400, duplicateValue: 700 },
  { id: "mountain", name: "Mountain", rarity: "Rare", price: 1400, duplicateValue: 700 },
  { id: "cyber", name: "Cyber", rarity: "Epic", price: 2800, duplicateValue: 1400 },
  { id: "royal", name: "Royal", rarity: "Epic", price: 2500, duplicateValue: 1250 },
  { id: "shadow", name: "Shadow", rarity: "Epic", price: 2500, duplicateValue: 1250 },
  { id: "obsidian", name: "Obsidian", rarity: "Legendary", price: 6000, duplicateValue: 3000 },
  { id: "polska_gora", name: "Polska gurom", rarity: "Legendary", price: 7000, duplicateValue: 3500 },
  { id: "ziolo", name: "Zioło", rarity: "Special", price: null, duplicateValue: 3500 },
];

export const CASES: readonly CaseDef[] = [
  { id: "gold", name: "Złota Skrzynia", type: "chips", price: 0, requiresKey: true },
  { id: "skins", name: "Skrzynia Skinów", type: "skins", price: 0, requiresKey: true },
];

export const GOLD_AMOUNTS: Record<ChipRarity, readonly number[]> = {
  Common: [300, 400, 500],
  Uncommon: [700, 800, 1000],
  Rare: [1400, 1600, 1800],
  Epic: [3000, 3500],
  Legendary: [10000, 15000],
  "Mega Jackpot": [1_000_000],
};

/** Percent weights. Rolls are 1–1000, so each percent is 10 ticks. */
export const CHIP_RARITY_CHANCES: Record<ChipRarity, number> = {
  Common: 54,
  Uncommon: 25,
  Rare: 12,
  Epic: 6,
  Legendary: 2,
  "Mega Jackpot": 0.2,
};

export const SKIN_RARITY_CHANCES: Record<SkinRarity, number> = {
  Common: 54,
  Uncommon: 25,
  Rare: 12,
  Epic: 6,
  Legendary: 2,
  Special: 0.1,
};

export const RARITY_LABEL: Record<SkinRarity | ChipRarity, string> = {
  Common: "Zwykły",
  Uncommon: "Niezwykły",
  Rare: "Rzadki",
  Epic: "Epicki",
  Legendary: "Legendarny",
  Special: "Specjalny",
  "Mega Jackpot": "Mega Jackpot",
};

const CHIP_RARITY_ORDER: readonly ChipRarity[] = [
  "Common",
  "Uncommon",
  "Rare",
  "Epic",
  "Legendary",
  "Mega Jackpot",
];

const SKIN_RARITY_ORDER: readonly SkinRarity[] = [
  "Common",
  "Uncommon",
  "Rare",
  "Epic",
  "Legendary",
  "Special",
];

export const SKIN_BY_ID: Readonly<Record<string, SkinDef>> = Object.fromEntries(
  SKIN_CATALOG.map((skin) => [skin.id, skin]),
);

export function findSkin(id: string): SkinDef | undefined {
  return SKIN_BY_ID[id];
}

/** Unknown or retired ids fall back to Classic. */
export function resolveSkinId(id: string | null | undefined): string {
  return id && SKIN_BY_ID[id] ? id : DEFAULT_SKIN_ID;
}

export function findCase(id: string): CaseDef | undefined {
  return CASES.find((item) => item.id === id);
}

/** Shop buy: priced above free Classic. Case-only skins return false. */
export function isBuyable(skin: SkinDef): boolean {
  return skin.price !== null && skin.price > 0;
}

export function duplicateRefund(skin: SkinDef): number {
  return Math.max(0, Math.floor(skin.duplicateValue));
}

export function skinsByRarity(rarity: SkinRarity): SkinDef[] {
  return SKIN_CATALOG.filter((skin) => skin.id !== DEFAULT_SKIN_ID && skin.rarity === rarity);
}

export function caseSkinPool(): SkinDef[] {
  return SKIN_CATALOG.filter((skin) => skin.id !== DEFAULT_SKIN_ID);
}

/** 0 ≤ n < 1. Prefer Web Crypto when the runtime has it. */
export function randomUnit(): number {
  const cryptoApi = (globalThis as { crypto?: { getRandomValues: (buffer: Uint32Array) => Uint32Array } }).crypto;
  if (cryptoApi) {
    const bytes = new Uint32Array(1);
    cryptoApi.getRandomValues(bytes);
    return (bytes[0] ?? 0) / 0x1_0000_0000;
  }
  return Math.random();
}

export function randomIntInclusive(min: number, max: number, rng: () => number = randomUnit): number {
  if (max < min) return min;
  return min + Math.floor(rng() * (max - min + 1));
}

export function pickOne<T>(items: readonly T[], rng: () => number = randomUnit): T {
  if (items.length === 0) {
    throw new Error("pickOne: empty list");
  }
  return items[randomIntInclusive(0, items.length - 1, rng)] as T;
}

/**
 * Legacy roll: `randInt(1000) + 1`, then accumulate `chance * 10`.
 * Leftover mass (the chances do not sum to 100) falls through to the first rarity.
 */
export function pickWeighted<T extends string>(
  order: readonly T[],
  chances: Record<T, number>,
  roll: number,
): T {
  let acc = 0;
  for (const rarity of order) {
    acc += chances[rarity] * 10;
    if (roll <= acc) return rarity;
  }
  return order[0] as T;
}

export function pickChipRarity(roll: number): ChipRarity {
  return pickWeighted(CHIP_RARITY_ORDER, CHIP_RARITY_CHANCES, roll);
}

export function pickSkinRarity(roll: number): SkinRarity {
  return pickWeighted(SKIN_RARITY_ORDER, SKIN_RARITY_CHANCES, roll);
}

export function rollChipRarity(rng: () => number = randomUnit): ChipRarity {
  return pickChipRarity(randomIntInclusive(1, 1000, rng));
}

export function rollSkinRarity(rng: () => number = randomUnit): SkinRarity {
  return pickSkinRarity(randomIntInclusive(1, 1000, rng));
}

export function pickGoldAmount(rarity: ChipRarity, rng: () => number = randomUnit): number {
  return pickOne(GOLD_AMOUNTS[rarity], rng);
}

export function pickSkinForRarity(rarity: SkinRarity, rng: () => number = randomUnit): SkinDef {
  const pool = skinsByRarity(rarity);
  return pool.length > 0 ? pickOne(pool, rng) : pickOne(caseSkinPool(), rng);
}

export type ShopReelItem =
  | { kind: "chips"; amount: number; rarity: ChipRarity }
  | { kind: "skin"; skinId: string; rarity: SkinRarity };

export interface GoldDrop {
  type: "chips";
  amount: number;
  rarity: ChipRarity;
  chipsDelta: number;
  grantSkinId: null;
  duplicate: false;
}

export interface SkinDrop {
  type: "skin";
  skinId: string;
  rarity: SkinRarity;
  chipsDelta: number;
  grantSkinId: string | null;
  duplicate: boolean;
  refund: number;
}

export type CaseDrop = GoldDrop | SkinDrop;

export interface CaseOpenPlan {
  caseId: CaseDef["id"];
  drop: CaseDrop;
  reel: ShopReelItem[];
  winnerIndex: number;
}

export function resolveGoldDrop(rng: () => number = randomUnit): GoldDrop {
  const rarity = rollChipRarity(rng);
  const amount = pickGoldAmount(rarity, rng);
  return { type: "chips", amount, rarity, chipsDelta: amount, grantSkinId: null, duplicate: false };
}

export function resolveSkinDrop(owned: ReadonlySet<string>, rng: () => number = randomUnit): SkinDrop {
  const rarity = rollSkinRarity(rng);
  const skin = pickSkinForRarity(rarity, rng);
  const ownedAlready = owned.has(skin.id);
  const refund = ownedAlready ? duplicateRefund(skin) : 0;
  return {
    type: "skin",
    skinId: skin.id,
    rarity: skin.rarity,
    chipsDelta: refund,
    grantSkinId: ownedAlready ? null : skin.id,
    duplicate: ownedAlready,
    refund,
  };
}

function goldReelItem(rng: () => number): ShopReelItem {
  const rarity = rollChipRarity(rng);
  return { kind: "chips", amount: pickGoldAmount(rarity, rng), rarity };
}

function skinReelItem(rng: () => number): ShopReelItem {
  const skin = pickOne(caseSkinPool(), rng);
  return { kind: "skin", skinId: skin.id, rarity: skin.rarity };
}

export function winnerToReelItem(drop: CaseDrop): ShopReelItem {
  if (drop.type === "chips") {
    return { kind: "chips", amount: drop.amount, rarity: drop.rarity };
  }
  return { kind: "skin", skinId: drop.skinId, rarity: drop.rarity };
}

export function buildCaseReel(
  type: CaseDef["type"],
  winner: ShopReelItem,
  rng: () => number = randomUnit,
): ShopReelItem[] {
  const reel: ShopReelItem[] = [];
  for (let index = 0; index < REEL_LENGTH; index += 1) {
    if (index === REEL_WINNER_INDEX) {
      reel.push(winner);
    } else {
      reel.push(type === "chips" ? goldReelItem(rng) : skinReelItem(rng));
    }
  }
  return reel;
}

export function planCaseOpen(
  caseId: CaseDef["id"],
  owned: ReadonlySet<string>,
  rng: () => number = randomUnit,
): CaseOpenPlan {
  const drop = caseId === "gold" ? resolveGoldDrop(rng) : resolveSkinDrop(owned, rng);
  const reel = buildCaseReel(caseId === "gold" ? "chips" : "skins", winnerToReelItem(drop), rng);
  return { caseId, drop, reel, winnerIndex: REEL_WINNER_INDEX };
}
